import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, RefreshCw, Search, Trash2, XCircle } from 'lucide-react';

import {
  ediCatalogSearch,
  useEdiIntakeDetail,
  useEdiReject,
  useEdiReprocess,
  useEdiSubmit,
  HOLD_REASON_LABELS,
  type EdiCatalogHit,
  type EdiSubmitResult,
} from '@/hooks/admin/useEdi';
import { cn } from '@/lib/utils';
import { describeSchedule, effectiveSchedule, fmtCutoffTime } from '@/lib/deliverySchedule';

/**
 * /admin/edi/:intakeId — review one emailed PO: parsed data + the original
 * attachment side by side, a per-line match editor (unmatched lines get a
 * catalog picker), the store/location picker, then Submit order (through the
 * same submit-order pipeline as every other channel) or Reject. Every
 * confirmed line match teaches orders.edi_item_aliases, so the next PO from
 * this chain matches by itself.
 */

const inputCls =
  'w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted/60 focus:outline-none focus:border-brix-primary/50';

interface EditLine {
  item_code: string | null;
  description: string;
  qty: number;
  /** Unit price PRINTED ON THE PO (informational — the order bills at OUR
   *  resolved pricing; a visible mismatch is a contract-pricing flag). */
  unit_price: number | null;
  catalog_item_id: string | null;
  matched_label: string | null;
  method: string | null;
}

const DECIDABLE = new Set(['needs_review', 'failed', 'received', 'processing']);

export default function AdminEdiDetail() {
  const { intakeId } = useParams<{ intakeId: string }>();
  const { data, isLoading, refetch } = useEdiIntakeDetail(intakeId);
  const submit = useEdiSubmit();
  const reject = useEdiReject();
  const reprocess = useEdiReprocess();

  const [lines, setLines] = useState<EditLine[]>([]);
  const [locationId, setLocationId] = useState('');
  // Chain gateways: the review picks a member STORE customer (own billing
  // account) instead of a location. members non-empty = chain mode.
  const [memberId, setMemberId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notifySender, setNotifySender] = useState(true);
  const [result, setResult] = useState<EdiSubmitResult | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Seed the editor once per intake load.
  useEffect(() => {
    if (!data || seeded) return;
    const intake = data.intake;
    const fromMatched = (intake.matched ?? []).map((m) => ({
      item_code: m.item_code,
      description: m.description,
      qty: m.qty,
      unit_price: m.unit_price,
      catalog_item_id: m.catalog_item_id,
      matched_label: m.matched_label,
      method: m.method,
    }));
    const fromParsed = (intake.parsed?.lines ?? []).map((l) => ({
      item_code: l.item_code,
      description: l.description,
      qty: l.qty,
      unit_price: l.unit_price,
      catalog_item_id: null,
      matched_label: null,
      method: null,
    }));
    setLines(fromMatched.length > 0 ? fromMatched : fromParsed);
    setLocationId(intake.customer_location_id ?? '');
    setMemberId(intake.member_customer_id ?? '');
    setPoNumber(intake.po_number ?? intake.parsed?.po_number ?? '');
    setDeliveryDate(intake.parsed?.requested_delivery_date ?? '');
    setNotes(intake.parsed?.notes ? `EDI PO — ${intake.parsed.notes}`.slice(0, 500) : '');
    setSeeded(true);
  }, [data, seeded]);

  const intake = data?.intake;
  const decidable = intake ? DECIDABLE.has(intake.status) : false;
  const allMatched = lines.length > 0 && lines.every((l) => l.catalog_item_id && l.qty > 0);
  const isChain = (data?.members ?? []).length > 0;
  const canSubmit =
    decidable && allMatched && (isChain ? !!memberId : !!locationId) && !submit.isPending;

  const attachment = useMemo(
    () => (data?.attachments ?? []).find((a) => a.url) ?? null,
    [data],
  );

  // Route hint next to the date field: the chosen store's/location's
  // effective delivery schedule, e.g. "Tuesdays & Fridays · order by
  // 4:00 PM the day before". Chain mode reads the member customer's
  // schedule; single mode the location override ?? customer default.
  const scheduleHint = useMemo(() => {
    if (!data) return null;
    const s = isChain
      ? effectiveSchedule(
          null,
          data.members.find((m) => m.customer_id === memberId)?.delivery_schedule ?? null,
        )
      : effectiveSchedule(
          data.locations.find((l) => l.id === locationId)?.delivery_schedule ?? null,
          data.customer?.delivery_schedule ?? null,
        );
    return s ? `${describeSchedule(s)} · order by ${fmtCutoffTime(s)} the day before` : null;
  }, [data, isChain, memberId, locationId]);

  if (isLoading || !intake) {
    return <div className="px-6 py-10 text-sm text-brix-muted">Loading…</div>;
  }

  const handleSubmit = () => {
    setErrMsg(null);
    submit.mutate(
      {
        intake_id: intake.id,
        member_customer_id: isChain ? memberId : undefined,
        customer_location_id: isChain ? undefined : locationId,
        lines: lines.map((l) => ({
          catalog_item_id: l.catalog_item_id!,
          quantity: l.qty,
          item_code: l.item_code,
          description: l.description,
        })),
        po_number: poNumber.trim() || null,
        requested_delivery_date: deliveryDate || null,
        delivery_notes: notes.trim() || null,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          void refetch();
        },
        onError: (e) => setErrMsg(e instanceof Error ? e.message : 'submit failed'),
      },
    );
  };

  return (
    <div className="px-6 py-6 space-y-4">
      <Link to="/admin/edi" className="inline-flex items-center gap-1 text-xs font-semibold text-brix-primary hover:underline">
        <ArrowLeft size={12} /> Back to EDI queue
      </Link>

      {/* Header */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-semibold text-brix-ink">
              {data?.customer?.name ?? 'Unknown customer'}
              {poNumber ? <span className="text-brix-muted font-normal"> · PO {poNumber}</span> : null}
            </h2>
            <div className="text-xs text-brix-muted mt-1">
              From {intake.from_email ?? 'unknown'} ·{' '}
              {new Date(intake.received_at ?? intake.created_at).toLocaleString()}
              {intake.subject ? ` · "${intake.subject}"` : ''}
            </div>
            {intake.status === 'needs_review' && (
              <div className="mt-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                {HOLD_REASON_LABELS[intake.hold_reason ?? ''] ?? 'Needs review'}
                {intake.error ? ` — ${intake.error}` : ''}
              </div>
            )}
            {intake.status === 'failed' && (
              <div className="mt-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                Failed{intake.error ? ` — ${intake.error}` : ''}
              </div>
            )}
            {intake.status === 'submitted' && (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 size={12} /> Submitted
                {intake.reviewed_by ? ` by ${intake.reviewed_by}` : ' automatically'}
              </div>
            )}
            {intake.status === 'rejected' && (
              <div className="mt-2 inline-flex items-center rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                Rejected{intake.error ? ` — ${intake.error}` : ''}
              </div>
            )}
          </div>
          {decidable && (
            <button
              type="button"
              onClick={() => reprocess.mutate(intake.id)}
              disabled={reprocess.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-2 text-xs font-semibold text-brix-muted hover:text-brix-ink"
              title="Re-run the automatic parse + match (e.g. after fixing EDI settings)"
            >
              <RefreshCw size={12} className={reprocess.isPending ? 'animate-spin' : ''} />
              Re-run automatic match
            </button>
          )}
        </div>
      </div>

      {/* Success panel */}
      {result && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
          <div className="font-semibold flex items-center gap-1.5">
            <CheckCircle2 size={15} /> Order submitted
            {result.idempotent_replay ? ' (already existed — no duplicate created)' : ''}
          </div>
          <div className="mt-1">
            Order {result.order_number ?? result.order_id.slice(0, 8)} · Service Fusion job{' '}
            {result.sf_job_number ?? result.sf_job_id ?? '—'} (Unscheduled).
            {result.aliases_learned > 0 &&
              ` Learned ${result.aliases_learned} item alias${result.aliases_learned === 1 ? '' : 'es'} for next time.`}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: the PO document */}
        <div className="lg:col-span-2 space-y-3">
          <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3">
            <h3 className="text-xs font-semibold text-brix-muted uppercase tracking-wide mb-2">
              Original PO
            </h3>
            {(data?.attachments ?? []).length === 0 ? (
              <p className="text-sm text-brix-muted">
                No attachment — parsed from the email body.
              </p>
            ) : (
              <div className="space-y-2">
                {(data?.attachments ?? []).map((a, i) =>
                  a.url ? (
                    <a
                      key={i}
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-brix-primary hover:underline"
                    >
                      <FileText size={14} /> {a.filename}
                    </a>
                  ) : (
                    <span key={i} className="flex items-center gap-2 text-sm text-brix-muted">
                      <FileText size={14} /> {a.filename} (unavailable)
                    </span>
                  ),
                )}
              </div>
            )}
            {attachment?.url && (
              <iframe
                title="PO preview"
                src={attachment.url}
                className="mt-3 w-full h-[480px] rounded-lg border border-brix-border bg-white"
              />
            )}
            {!attachment?.url && intake.raw_text && (
              <pre className="mt-3 max-h-[400px] overflow-auto whitespace-pre-wrap rounded-lg border border-brix-border bg-brix-surface-alt p-3 text-xs text-brix-ink">
                {intake.raw_text.slice(0, 8000)}
              </pre>
            )}
          </div>

          {/* Parsed store / ship-to facts */}
          {intake.parsed && (
            <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3 text-sm space-y-1">
              <h3 className="text-xs font-semibold text-brix-muted uppercase tracking-wide mb-1">
                Parsed from the PO
              </h3>
              {intake.parsed.store_name && (
                <div><span className="text-brix-muted">Store:</span> {intake.parsed.store_name}{intake.parsed.store_number ? ` (#${intake.parsed.store_number})` : ''}</div>
              )}
              {intake.parsed.account_number && (
                <div><span className="text-brix-muted">Acct # (their account with us):</span> {intake.parsed.account_number}</div>
              )}
              {intake.parsed.ship_to && (
                <div>
                  <span className="text-brix-muted">Ship to:</span>{' '}
                  {[intake.parsed.ship_to.line1, intake.parsed.ship_to.city, intake.parsed.ship_to.state, intake.parsed.ship_to.zip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {intake.parsed.requested_delivery_date && (
                <div><span className="text-brix-muted">Requested date:</span> {intake.parsed.requested_delivery_date}</div>
              )}
              {intake.parsed.notes && (
                <div className="mt-1 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-amber-800">
                  <span className="font-semibold">PO comments:</span> {intake.parsed.notes}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: the order builder */}
        <div className="lg:col-span-3 space-y-3">
          <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3 space-y-3">
            <h3 className="text-xs font-semibold text-brix-muted uppercase tracking-wide">
              Order to submit
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block sm:col-span-1">
                <span className="text-[11px] font-semibold text-brix-muted">
                  {isChain ? 'Store (its own billing account)' : 'Location (store)'}
                </span>
                {isChain ? (
                  <select
                    className={cn(inputCls, 'mt-1')}
                    value={memberId}
                    disabled={!decidable}
                    onChange={(e) => setMemberId(e.target.value)}
                  >
                    <option value="">— pick the store —</option>
                    {(data?.members ?? []).map((m) => (
                      <option key={m.customer_id} value={m.customer_id}>
                        {m.store_code ? `#${m.store_code} · ` : ''}
                        {m.name}
                        {m.city ? ` · ${m.city}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    className={cn(inputCls, 'mt-1')}
                    value={locationId}
                    disabled={!decidable}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    <option value="">— pick the store —</option>
                    {(data?.locations ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.display_name ?? l.id.slice(0, 8)}
                        {l.location_code ? ` (${l.location_code})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {isChain ? (
                  <span className="text-[11px] text-brix-muted">
                    Each store bills its own account — the order, SF job, and invoice land
                    under the store you pick.
                  </span>
                ) : null}
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-brix-muted">PO number</span>
                <input
                  className={cn(inputCls, 'mt-1')}
                  value={poNumber}
                  disabled={!decidable}
                  onChange={(e) => setPoNumber(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-brix-muted">Requested delivery date</span>
                <input
                  type="date"
                  className={cn(inputCls, 'mt-1')}
                  value={deliveryDate}
                  disabled={!decidable}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
                {scheduleHint ? (
                  <span className="text-[11px] text-brix-muted">
                    Delivers {scheduleHint} — leave blank to auto-pick the next route day.
                  </span>
                ) : null}
              </label>
            </div>

            {/* Line editor */}
            <div className="space-y-2">
              {lines.map((line, i) => (
                <LineRow
                  key={i}
                  line={line}
                  disabled={!decidable}
                  onChange={(next) => setLines((ls) => ls.map((l, j) => (j === i ? next : l)))}
                  onRemove={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                />
              ))}
              {decidable && (
                <button
                  type="button"
                  className="text-xs font-semibold text-brix-primary hover:underline"
                  onClick={() =>
                    setLines((ls) => [
                      ...ls,
                      { item_code: null, description: '', qty: 1, unit_price: null, catalog_item_id: null, matched_label: null, method: null },
                    ])
                  }
                >
                  + Add a line
                </button>
              )}
              {lines.length === 0 && (
                <p className="text-sm text-brix-muted">
                  No lines parsed — add them manually from the PO on the left.
                </p>
              )}
            </div>

            <label className="block">
              <span className="text-[11px] font-semibold text-brix-muted">Delivery notes (goes on the SF job)</span>
              <input
                className={cn(inputCls, 'mt-1')}
                value={notes}
                disabled={!decidable}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>

            {errMsg && <div className="text-sm text-red-600">{errMsg}</div>}

            {decidable && (
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-semibold',
                    canSubmit
                      ? 'bg-brix-primary text-white hover:opacity-90'
                      : 'bg-brix-surface-alt text-brix-muted cursor-not-allowed',
                  )}
                >
                  {submit.isPending ? 'Submitting…' : 'Submit order → Service Fusion'}
                </button>
                <button
                  type="button"
                  onClick={() => setRejectOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-2 text-sm font-semibold text-brix-muted hover:text-red-600"
                >
                  <XCircle size={14} /> Reject…
                </button>
                {!allMatched && lines.length > 0 && (
                  <span className="text-xs text-amber-600">
                    Match every line to a catalog item to enable submit.
                  </span>
                )}
              </div>
            )}

            {rejectOpen && decidable && (
              <div className="rounded-lg border border-brix-border bg-brix-surface-alt p-3 space-y-2">
                <input
                  className={inputCls}
                  placeholder="Reason (optional — included in the email if notifying the sender)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
                <label className="flex items-center gap-2 text-xs text-brix-muted">
                  <input
                    type="checkbox"
                    checked={notifySender}
                    onChange={(e) => setNotifySender(e.target.checked)}
                  />
                  Email the sender that this PO couldn't be processed
                </label>
                <button
                  type="button"
                  disabled={reject.isPending}
                  onClick={() =>
                    reject.mutate(
                      { intake_id: intake.id, reason: rejectReason.trim() || undefined, notify_sender: notifySender },
                      { onError: (e) => setErrMsg(e instanceof Error ? e.message : 'reject failed') },
                    )
                  }
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- line row + catalog picker -------------------- */

function LineRow({
  line,
  disabled,
  onChange,
  onRemove,
}: {
  line: EditLine;
  disabled: boolean;
  onChange: (l: EditLine) => void;
  onRemove: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const methodChip =
    line.method === 'alias'
      ? 'learned'
      : line.method === 'sku'
        ? 'SKU match'
        : line.method === 'search'
          ? 'auto match'
          : line.method === 'manual'
            ? 'picked'
            : null;

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5',
        line.catalog_item_id ? 'border-brix-border bg-brix-surface' : 'border-amber-300 bg-amber-50/50',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-brix-ink">
            {line.description || <span className="text-brix-muted italic">new line</span>}
            {line.item_code && (
              <span className="ml-2 text-xs font-mono text-brix-muted">{line.item_code}</span>
            )}
            {line.unit_price != null && (
              <span className="ml-2 text-xs text-brix-muted">
                PO price ${line.unit_price.toFixed(2)}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs">
            {line.catalog_item_id ? (
              <span className="text-emerald-700">
                → {line.matched_label ?? 'catalog item'}
                {methodChip && <span className="ml-1.5 text-brix-muted">({methodChip})</span>}
                {!disabled && (
                  <button
                    type="button"
                    className="ml-2 text-brix-primary font-semibold hover:underline"
                    onClick={() => setPickerOpen((v) => !v)}
                  >
                    change
                  </button>
                )}
              </span>
            ) : (
              <span className="text-amber-700 font-semibold">
                Unmatched
                {!disabled && (
                  <button
                    type="button"
                    className="ml-2 text-brix-primary hover:underline"
                    onClick={() => setPickerOpen((v) => !v)}
                  >
                    pick a catalog item
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
        <label className="shrink-0 text-right">
          <span className="block text-[10px] font-semibold text-brix-muted">Qty</span>
          <input
            type="number"
            min={1}
            className="w-16 rounded-lg border border-brix-border bg-brix-surface px-2 py-1 text-sm text-right"
            value={line.qty}
            disabled={disabled}
            onChange={(e) => onChange({ ...line, qty: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
          />
        </label>
        {!disabled && (
          <button
            type="button"
            className="shrink-0 mt-4 text-brix-muted hover:text-red-600"
            title="Remove line"
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {pickerOpen && !disabled && (
        <CatalogPicker
          initialTerm={line.description || line.item_code || ''}
          onPick={(hit) => {
            onChange({
              ...line,
              catalog_item_id: hit.catalog_item_id,
              matched_label: hit.label,
              method: 'manual',
              description: line.description || hit.label,
            });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

function CatalogPicker({
  initialTerm,
  onPick,
}: {
  initialTerm: string;
  onPick: (hit: EdiCatalogHit) => void;
}) {
  const [term, setTerm] = useState(initialTerm);
  const [hits, setHits] = useState<EdiCatalogHit[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = term.trim();
    if (!t) {
      setHits([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      ediCatalogSearch(t)
        .then(setHits)
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [term]);

  return (
    <div className="mt-2 rounded-lg border border-brix-border bg-brix-surface-alt p-2">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-2.5 text-brix-muted" />
        <input
          autoFocus
          className="w-full rounded-lg border border-brix-border bg-brix-surface pl-8 pr-3 py-1.5 text-sm"
          placeholder="Search the catalog…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
      </div>
      <div className="mt-1.5 max-h-44 overflow-auto">
        {searching && <div className="px-2 py-1.5 text-xs text-brix-muted">Searching…</div>}
        {!searching && hits.length === 0 && term.trim() && (
          <div className="px-2 py-1.5 text-xs text-brix-muted">
            No matches — try just the flavor/brand words.
          </div>
        )}
        {hits.map((h) => (
          <button
            key={h.catalog_item_id}
            type="button"
            onClick={() => onPick(h)}
            className="block w-full text-left rounded px-2 py-1.5 text-sm hover:bg-brix-primary-tint"
          >
            {h.label}
            {h.sku && <span className="ml-2 text-xs font-mono text-brix-muted">{h.sku}</span>}
            {h.category && <span className="ml-2 text-xs text-brix-muted">{h.category}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
